import { useState, useEffect } from 'react';
import { useTier, controlHeight } from '../lib/tiers';
import { supabase } from '../lib/supabase';
import { ROLE_ORDER, ROLE_LABELS, barLabelsForRole } from '../lib/bottomBar';
import { landingChoicesFor, pageRuleAuth } from '../lib/deepLink';
import { useAuth } from '../components/AuthContext';
import { canAccessCompanySettings } from '../lib/permissions';
import { CategoryGovernance, UnitGovernance, DepartmentGovernance, DenialReasonGovernance } from '../components/settings/MasterDataSections';
import { CompanyProfileSection } from '../components/settings/CompanyProfileSection';
import { adjustAlertDecision, formatLimitMoney } from '../lib/stockMovements';

export default function CompanySettingsPage() {
  const { isAdmin, isExecutive, setLabelPrintingEnabled, setRequireJobRefOnUse } = useAuth();
  const canAccess = canAccessCompanySettings({ isAdmin, isExecutive });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  // `silent` added Session 78n8 (#403). Flipping `loading` on a REFRESH swaps
  // the whole page for "Loading…" and back, which unmounts every section and
  // destroys any state they hold -- including a just-saved confirmation
  // message, which is how this was found. The initial load still shows the
  // loading state; a refresh after a save no longer does.
  //
  // Only the Company profile section opts in, deliberately. Every other section
  // keeps the behaviour it shipped with, so this cannot regress a screen it was
  // not written for.
  async function loadSettings(options = {}) {
    const silent = options.silent === true;
    if (!silent) setLoading(true);
    const { data } = await supabase.from('company_settings').select('*').single();
    setSettings(data);
    if (!silent) setLoading(false);
  }

  async function handleToggle(field, value) {
    if (!settings) return;
    const { error: saveErr } = await supabase
      .from('company_settings')
      .update({ [field]: value, updated_at: new Date().toISOString() })
      .eq('id', settings.id);
    // #199 part 3 (drawing C, C4) -- everyone else reads the switch at sign-in;
    // the person flipping it sees the change at once, and only if it saved.
    if (!saveErr && field === 'qr_label_printing_enabled' && setLabelPrintingEnabled) {
      setLabelPrintingEnabled(value);
    }
    if (!saveErr && field === 'require_job_ref_on_consume' && setRequireJobRefOnUse) {
      setRequireJobRefOnUse(value);
    }
    await loadSettings();
  }

  if (!canAccess) return null;
  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</div>;
  if (!settings) return null;

  const sections = [
    {
      title: 'Modules',
      rows: [
        {
          field: 'purchasing_portal_enabled',
          label: 'Purchasing portal',
          desc: 'Enables the full request → approve → order → track → receive workflow for all branches.',
        },
      ],
    },
    {
      title: 'Cost and pricing',
      rows: [
        {
          field: 'show_cost_data',
          label: 'Show cost data',
          desc: 'Displays unit cost and pricing information to authorized roles. Team members always see costs hidden.',
        },
      ],
    },
    {
      title: 'Inventory behavior',
      rows: [
        {
          field: 'allow_negative_stock',
          label: 'Allow negative stock',
          desc: 'Permits stock levels to go below zero. Useful when receiving is logged after the fact.',
        },
        // #199 part 1, Session 79.22b.6.1g -- APPROVED DRAWING D (D1). Under
        // Allow negative stock because both are rules about using stock. Off
        // by default, as production holds it.
        {
          field: 'require_job_ref_on_consume',
          label: 'Require a WO# to use stock',
          desc: "Using stock can't be saved without the work order it went to. When off, a WO# or a note is enough.",
        },
        // #199 part 3, Session 79.22b.6.1f -- APPROVED DRAWING C. The column
        // existed with no reader and no switch since before Session 74c. One
        // switch for every label, stock and tools (Colby's D3); on by default.
        {
          field: 'qr_label_printing_enabled',
          label: 'QR label printing',
          desc: "Shows Print label buttons for stock and tools. Turn it off if your company doesn't print QR labels; they disappear for everyone.",
        },
        {
          field: 'print_on_receive_default',
          label: 'Print labels on receive by default',
          desc: 'Pre-checks the print-labels option when receiving stock. Can still be turned off for an individual receive.',
          // C3 -- stays on the page and says why it is off, rather than
          // vanishing and leaving somebody hunting for it.
          dependsOn: 'qr_label_printing_enabled',
          offDesc: 'Off while QR label printing is off.',
        },
      ],
    },
    {
      title: 'Vendor access',
      rows: [
        {
          field: 'team_members_can_contact_vendors',
          label: 'Team members can contact vendors',
          desc: 'Shows call and email buttons on vendor cards to team members. Off by default.',
        },
      ],
    },
    {
      title: 'New manager defaults',
      rows: [
        {
          field: 'new_manager_purchasing_default',
          label: 'New managers can act in Purchasing Queue',
          desc: 'Sets the starting Purchasing Queue permission for newly invited managers. Existing managers are unaffected — adjust their access individually in User Management.',
        },
        {
          field: 'new_manager_vendor_catalog_default',
          label: 'New managers can edit Vendor Catalog',
          desc: 'Sets the starting Vendor Catalog permission for newly invited managers. Existing managers are unaffected — adjust their access individually in User Management.',
        },
      ],
      // #289, Session 74z2. The invoice-reconciliation default belongs in this
      // group and used to render three sections down, inside Invoice
      // reconciliation with the discrepancy thresholds. It looked missing
      // because it was filed under the mechanism that consumes it rather than
      // the decision it expresses, and a person looking for a new-user default
      // looks in the group named after new users.
      //
      // It is an `extra` rather than another `rows` entry because it does not
      // save the generic way: it goes through update_invoice_discrepancy_settings,
      // which writes company_settings_audit_log. A placement change must not
      // quietly drop audit coverage.
      extra: <NewManagerReconcileRow settings={settings} onSaved={loadSettings} />,
    },
  ];

  return (
    <div style={{ padding: '2rem', maxWidth: 660 }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, margin: '0 0 4px', color: '#0F0F0F' }}>Company settings</h1>
        <p style={{ fontSize: 14, color: '#5C5C5C', margin: 0 }}>Manage feature access and behavior for {settings.company_name}.</p>
      </div>

      {/* #403, Session 78n8. First on the page deliberately: every other
          section adjusts how Stede behaves, and this one says who Stede is
          writing on behalf of. Nothing below it can produce a document
          without it. */}
      <CompanyProfileSection settings={settings} onSaved={() => loadSettings({ silent: true })} />

      {sections.map(section => (
        <div key={section.title} style={{ marginBottom: '2rem' }}>
          <p style={{
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            margin: '0 0 10px',
            paddingBottom: 8,
            borderBottom: '0.5px solid #E0E0E0',
          }}>
            {section.title}
          </p>

          {section.rows.map((row, idx) => {
            const blocked = row.dependsOn && settings[row.dependsOn] === false;
            return (
              <SettingRow
                key={row.field}
                label={row.label}
                desc={blocked ? row.offDesc : row.desc}
                value={blocked ? false : !!settings[row.field]}
                disabled={!!blocked}
                onChange={val => handleToggle(row.field, val)}
                isLast={idx === section.rows.length - 1 && !section.extra}
              />
            );
          })}
          {section.extra}
        </div>
      ))}

      {/* #553 D13, Session 79.15g. Sits directly under the behaviour groups
          because it IS one -- it changes what the load sheet asks, the same way
          Inventory behavior changes what the receive sheet does.

          `silent` deliberately, per #403: a non-silent refresh swaps the whole
          page for "Loading…" and back, which unmounts this section and destroys
          its own "Saved." confirmation. That is how the flash was found the
          first time. */}
      {/* #363, Session 79.22b.6.6 -- under the switch groups and above Transfers,
          because it is inventory behavior with a grouped save (gate Screen 3).
          `silent` for the same reason as Transfers: its own "Saved." must survive
          the refresh. */}
      <AdjustAlertSection settings={settings} onSaved={() => loadSettings({ silent: true })} />

      <TransfersSection settings={settings} onSaved={() => loadSettings({ silent: true })} />

      <NavigationDefaultsSection settings={settings} onSaved={loadSettings} />
      <InvoiceReconciliationSection settings={settings} onSaved={loadSettings} />
      <CostBasisCorrectionSection settings={settings} onSaved={loadSettings} />
      <PurchaseApprovalSection settings={settings} onSaved={loadSettings} />
      <PoNumberingSection settings={settings} onSaved={loadSettings} />
      <CategoryGovernance />
      <UnitGovernance />
      <DepartmentGovernance />
      <DenialReasonGovernance />
    </div>
  );
}

// `disabled` -- #199 part 3: a row that depends on another switch. Greyed, with
// its description replaced by the reason, so the switch that cannot be pressed
// says why in the place a person looks (#492's standard).
function SettingRow({ label, desc, value, onChange, isLast, disabled = false }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      padding: '14px 0',
      borderBottom: isLast ? 'none' : '0.5px solid #E0E0E0',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: disabled ? '#9A9A9A' : '#0F0F0F', margin: '0 0 2px' }}>{label}</p>
        <p style={{ fontSize: 12, color: disabled ? '#9A9A9A' : '#5C5C5C', margin: 0, lineHeight: 1.5 }}>{desc}</p>
      </div>
      <Toggle value={value} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// #680, Session 79.22b.4 -- the same correction as the notification switch: the
// drawn track (40 x 22) is unchanged, and the BUTTON around it takes its height
// from tiers.js, so the thing a mouse or finger hits meets the floor.
function Toggle({ value, onChange, disabled = false }) {
  const { coarse } = useTier();
  return (
    <button
      onClick={() => { if (!disabled) onChange(!value); }}
      aria-checked={value}
      aria-disabled={disabled || undefined}
      role="switch"
      style={{
        minHeight: controlHeight(coarse, 'min'), minWidth: 40,
        background: 'none', border: 'none', padding: 0, cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span aria-hidden="true" style={{
        position: 'relative',
        width: 40,
        height: 22,
        borderRadius: 11,
        background: value ? '#1D9E75' : '#D0D0D0',
        transition: 'background 0.2s',
        display: 'block',
      }}>
      <span style={{
        position: 'absolute',
        top: 3,
        left: value ? 21 : 3,
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: 'white',
        transition: 'left 0.2s',
        display: 'block',
      }} />
      </span>
    </button>
  );
}

// ── COUNT ADJUSTMENT ALERTS ──────────────────────────────────────────────────
//
// #363, Session 79.22b.6.6 -- the approved gate's Screen 3:
// docs/gates/Stede_AdjustmentThreshold_Gate_363.html. One switch and three
// numbers, saved together through update_adjust_alert_settings(), which only an
// admin or an executive may use and which writes the settings history.
//
// OFF IS TODAY (D13): every adjustment but an opening count tells the managers,
// so the switch can never silence anything. Off dims the sentence and the numbers
// as Purchase approval does; they stay readable, and the evidence panel stays live.
//
// THE PANEL IS REAL DATA (D15): the last 30 days of corrections, judged by the
// same rule the save uses, with the numbers on screen and each row's own
// recorded cost. Today it says plainly that a correction with no cost on record
// alerts whatever the numbers are.
//
// Controls meet the finger floor (gate F4): Save 48px with a finger and 32 with a
// mouse; the fields 40 and 32. The page's older sections keep theirs until the
// design-system adoption sprint.
//
// Module level, not nested -- locked rule 2.
const ADJUST_ALERT_FIELD_ERRORS = {
  floor: 'Enter a dollar amount of 0 or more for Never alert under.',
  value: 'Enter a dollar amount above 0 for Alert over.',
  order: "Never alert under can't be more than Alert over.",
  share: 'Enter a percentage above 0 and up to 100.',
};

function adjustAlertFieldError(floor, value, share) {
  const f = floor === '' ? NaN : Number(floor);
  const v = value === '' ? NaN : Number(value);
  const s = share === '' ? NaN : Number(share);
  if (!Number.isFinite(f) || f < 0) return { field: 'floor', text: ADJUST_ALERT_FIELD_ERRORS.floor };
  if (!Number.isFinite(v) || v <= 0) return { field: 'value', text: ADJUST_ALERT_FIELD_ERRORS.value };
  if (f > v) return { field: 'floor', text: ADJUST_ALERT_FIELD_ERRORS.order };
  if (!Number.isFinite(s) || s <= 0 || s > 100) return { field: 'share', text: ADJUST_ALERT_FIELD_ERRORS.share };
  return null;
}

function AdjustAlertSection({ settings, onSaved }) {
  const { tier, coarse } = useTier();
  const stack = tier === 'phone' || tier === 'tablet';
  const fieldH = coarse ? controlHeight(true, 'min') : controlHeight(false, 'commit');
  const saveH = controlHeight(coarse, 'commit');

  const [enabled, setEnabled] = useState(settings.adjust_alert_enabled === true);
  const [floor, setFloor] = useState(String(Number(settings.adjust_alert_floor_dollars ?? 50)));
  const [value, setValue] = useState(String(Number(settings.adjust_alert_value_dollars ?? 250)));
  const [share, setShare] = useState(String(Number(settings.adjust_alert_share_percent ?? 10)));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);   // { field, text }
  const [saved, setSaved] = useState(false);
  const [rows, setRows] = useState(null);     // null counting, 'error', or the rows

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const since = new Date();
      since.setDate(since.getDate() - 30);
      const { data, error: qErr } = await supabase
        .from('inventory_transactions')
        .select('qty, qty_after, notes, unit_cost_at_transaction')
        .eq('tx_type', 'adjust')
        .gte('created_at', since.toISOString());
      if (cancelled) return;
      if (qErr) { setRows('error'); return; }
      setRows((data || []).filter(r => !String(r.notes || '').startsWith('Opening count')));
    })();
    return () => { cancelled = true; };
  }, []);

  // The panel's figures, judged by the save's own rule with the numbers on screen.
  let stats = null;
  if (Array.isArray(rows)) {
    const live = { enabled: true, floor: Number(floor), value: Number(value), share: Number(share) };
    const usable = !adjustAlertFieldError(floor, value, share);
    let wouldAlert = 0, noCost = 0;
    rows.forEach(r => {
      const counted = Number(r.qty_after);
      const before = counted - Number(r.qty);
      const d = adjustAlertDecision({
        reason: String(r.notes || '').split(' · ')[0], onHandBefore: before, counted,
        unitCost: r.unit_cost_at_transaction, settings: live,
      });
      if (d.why === 'no_cost') noCost++;
      if (d.alert) wouldAlert++;
    });
    stats = { total: rows.length, wouldAlert: usable ? wouldAlert : null, noCost };
  }

  async function handleSave() {
    setSaved(false);
    const bad = adjustAlertFieldError(floor, value, share);
    if (bad) { setError(bad); return; }
    setSaving(true);
    setError(null);
    const sent = { enabled, floor: Number(floor), value: Number(value), share: Number(share) };
    const { data, error: rpcError } = await supabase.rpc('update_adjust_alert_settings', {
      p_enabled: sent.enabled,
      p_floor_dollars: sent.floor,
      p_value_dollars: sent.value,
      p_share_percent: sent.share,
      p_reason: reason.trim() || null,
    });
    setSaving(false);
    if (rpcError) {
      if (rpcError.code === '42501') { setError({ field: null, text: 'Only an admin or an executive can change these alerts.' }); return; }
      if (rpcError.code === '22023') { setError({ field: null, text: rpcError.message }); return; }
      setError({ field: null, text: 'Failed to save: ' + rpcError.message });
      return;
    }
    // B3 item 4: "Saved." only when what came back is what was sent.
    const landed = data && data.enabled === sent.enabled
      && Number(data.floor_dollars) === sent.floor && Number(data.value_dollars) === sent.value
      && Number(data.share_percent) === sent.share;
    if (!landed) { setError({ field: null, text: 'Failed to save: the settings did not come back as sent.' }); return; }
    setReason('');
    setSaved(true);
    await onSaved();
  }

  const fieldBorder = name => (error && error.field === name ? '1.5px solid #A32D2D' : '1px solid #E0E0E0');
  const field = (name, label, val, set, unit, helper) => (
    <div style={stack ? {} : { flex: '0 0 160px' }}>
      <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>{label}</p>
      <div style={{ position: 'relative', maxWidth: stack ? 'none' : 160, opacity: enabled ? 1 : 0.4 }}>
        {unit === '$' && <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#5C5C5C' }}>$</span>}
        <input type="number" inputMode="decimal" value={val} aria-label={label}
          onChange={e => { set(e.target.value); setSaved(false); if (error && error.field === name) setError(null); }}
          style={{ width: '100%', height: fieldH, border: fieldBorder(name), borderRadius: 8, padding: unit === '$' ? '0 10px 0 22px' : '0 26px 0 10px', fontSize: 16, boxSizing: 'border-box', background: 'white' }} />
        {unit === '%' && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#5C5C5C' }}>%</span>}
      </div>
      {helper && <p style={{ fontSize: 12, color: '#5C5C5C', margin: '6px 0 0' }}>{helper}</p>}
    </div>
  );

  const stat = (label, val, sub) => (
    <div style={{ flex: '1 1 150px', minWidth: 140 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#0F0F0F', fontVariantNumeric: 'tabular-nums' }}>{val}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#5C5C5C', marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: 11.5, color: '#6A6A6A', marginTop: 2, lineHeight: 1.4 }}>{sub}</div>
    </div>
  );

  const floorN = Number(floor), valueN = Number(value), shareN = Number(share);
  const words = n => (Number.isFinite(n) ? formatLimitMoney(n) : '$—');
  const tick = { position: 'absolute', left: 2, top: 4, width: 8, height: 4, borderLeft: '2px solid #1D9E75', borderBottom: '2px solid #1D9E75', transform: 'rotate(-45deg)' };
  const rule = text => (
    <li style={{ fontSize: 12, color: '#3A3A3A', margin: 0, paddingLeft: 18, position: 'relative', lineHeight: 1.45 }}>
      <span aria-hidden="true" style={tick} />{text}
    </li>
  );

  return (
    <div style={{ marginBottom: '2rem' }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 10px', paddingBottom: 8, borderBottom: '0.5px solid #E0E0E0',
      }}>
        Count adjustment alerts
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>Alert only above a size</p>
          <p style={{ fontSize: 12, color: '#5C5C5C', margin: 0, lineHeight: 1.5 }}>When off, your managers are told about every count adjustment except an opening count. That's how Stede works today.</p>
        </div>
        <Toggle value={enabled} onChange={v => { setEnabled(v); setSaved(false); }} />
      </div>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0', opacity: enabled ? 1 : 0.4 }}>
        <p style={{ fontSize: 14, color: '#0F0F0F', lineHeight: 1.55, margin: 0 }}>
          Tell your managers when an adjustment is worth more than <b>{words(valueN)}</b>, or moves more than <b>{Number.isFinite(shareN) ? String(shareN) : '—'}%</b> of what the branch holds, but never for anything under <b>{words(floorN)}</b>.
        </p>
      </div>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', flexDirection: stack ? 'column' : 'row' }}>
          {field('floor', 'Never alert under', floor, setFloor, '$')}
          {field('value', 'Alert over', value, setValue, '$')}
          {field('share', 'Or over', share, setShare, '%', 'of what the branch holds')}
        </div>
      </div>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>Whatever these numbers say</p>
        <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 5 }}>
          {rule('An opening count never alerts.')}
          {rule("An item with no cost on record always alerts. Stede can't size the change.")}
          {rule('Using more than is on the shelf always alerts.')}
        </ul>
      </div>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>What the last 30 days say</p>
        <p style={{ fontSize: 12, color: '#5C5C5C', margin: '0 0 12px', lineHeight: 1.5 }}>Read this before changing the numbers. It's the evidence for the choice.</p>
        {rows === null ? (
          <div style={{ fontSize: 12, color: '#6A6A6A' }}>Counting…</div>
        ) : rows === 'error' ? (
          <div style={{ fontSize: 12, color: '#6A6A6A', lineHeight: 1.5 }}>The last 30 days didn't load. You can still save the setting.</div>
        ) : stats.total === 0 ? (
          <div style={{ fontSize: 12, color: '#6A6A6A', lineHeight: 1.5 }}>No counts were corrected in the last 30 days, so there's nothing to measure yet.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {stat('Counts corrected', stats.total, 'Opening counts are left out')}
            {stat('Would alert with these numbers', stats.wouldAlert === null ? '—' : `${stats.wouldAlert} of ${stats.total}`, 'Over a limit, or no cost on record')}
            {stat('Had no cost on record', stats.noCost, 'These alert whatever the numbers say')}
          </div>
        )}
      </div>

      <div style={{ padding: '14px 0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Reason for this change (optional)</p>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. turning on now that items carry costs"
          style={{ width: '100%', height: fieldH, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, boxSizing: 'border-box', marginBottom: 12 }} />
        {error && <div role="alert" style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error.text}</div>}
        <button onClick={handleSave} disabled={saving}
          style={{ height: saveH, width: stack ? '100%' : 'auto', padding: '0 20px', background: saving ? '#E0E0E0' : '#1D9E75', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
        {saved && <p style={{ fontSize: 12, color: 'var(--green-xdk)', margin: '10px 0 0' }}>Saved.</p>}
      </div>
    </div>
  );
}

// ── TRANSFERS ────────────────────────────────────────────────────────────────
//
// #553 D13, Session 79.15g. Who loads your trucks.
//
// COMPANY-WIDE ON PURPOSE, AND D12'S DRIVER PICKER IS DELIBERATELY NOT. This
// setting describes how the company organises work -- whether the person who
// loads a truck is the person who drives it. Who is driving one particular
// truck is a fact about that truck, so it is asked on the load sheet instead.
// Getting that split wrong is what the gate spent D12 and D13 separating.
//
// WHAT IT CHANGES, in the terms of somebody using Stede: on "Drivers load their
// own trucks" the load sheet asks once who is driving, defaults to the person
// pressing it, and that person signs at both ends. On "Loaders load, drivers
// drive" it asks twice and the driver signs at the far end.
//
// The default is D&R's own shape -- 'driver_loads', written by the column
// itself at 79.15f -- so nothing changes for them on the day this ships.
//
// Saves on change rather than behind a Save button: it is a single field, and
// every toggle row above it saves on change too.
//
// Module level, not nested -- locked rule 2.
function TransfersSection({ settings, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const value = settings.transfer_loader_model || 'driver_loads';

  async function handleChange(next) {
    setSaving(true);
    setError('');
    setSaved(false);

    // READ BACK WHAT CHANGED RATHER THAN TRUSTING A NULL ERROR. PostgREST
    // reports zero rows changed as SUCCESS, which is how 79.1's packing-slip
    // control said "Added just now" while writing nothing at all. A control
    // that reports a save it did not make is the silent-refusal family this
    // project has now paid for four times (#492, #497, #499).
    const { data, error: updErr } = await supabase
      .from('company_settings')
      .update({ transfer_loader_model: next, updated_at: new Date().toISOString() })
      .eq('id', settings.id)
      .select('transfer_loader_model');

    setSaving(false);

    if (updErr) { setError('Could not save that. ' + updErr.message); return; }
    if (!data || data.length === 0) {
      setError('Nothing was saved — the change did not reach the database. Reload and try again.');
      return;
    }
    if (data[0].transfer_loader_model !== next) {
      setError('The database kept a different setting. Reload to see what it holds.');
      return;
    }

    setSaved(true);
    await onSaved();
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 10px', paddingBottom: 8, borderBottom: '0.5px solid #E0E0E0',
      }}>
        Transfers
      </p>

      <div style={{ padding: '14px 0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>
          Who loads your trucks
        </p>
        <select
          value={value}
          onChange={e => handleChange(e.target.value)}
          disabled={saving}
          style={{
            width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8,
            padding: '0 10px', fontSize: 16, background: 'white',
          }}
        >
          <option value="driver_loads">Drivers load their own trucks</option>
          <option value="loader_and_driver">Loaders load, drivers drive</option>
        </select>
        <p style={{ fontSize: 12, color: 'var(--ink-2)', margin: '8px 0 0', lineHeight: 1.5 }}>
          At most companies the driver loads and signs for the load at both ends.
          Choose the second if different people do those jobs.
        </p>

        {error && (
          <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginTop: 10 }}>
            {error}
          </div>
        )}
        {/* --green-xdk, NOT --green-dk. Measured on white at 12px: xdk 7.57,
            dk 3.12, against a 4.5 floor. And `--green-dark` -- the name that
            reads as the obvious one -- does not exist at all, which is 78s.3's
            invisible button: an undefined CSS variable throws no error, logs
            nothing, and renders as nothing. */}
        {saved && !error && (
          <p style={{ fontSize: 12, color: 'var(--green-xdk)', margin: '10px 0 0' }}>Saved.</p>
        )}
      </div>

      {/* #746, Session 79.22b.6.4.1 -- Welding's two settings, here because the
          orders they date are mostly transfers. Days to coat and ship sits
          directly above the look-ahead (746 D26); the words are gate 746's
          copy table. */}
      <WeldingDaysRow
        settings={settings} column="welding_coat_ship_days" min={0} max={30} onSaved={onSaved}
        label="Days to coat and ship"
        helper="The Welding page dates each order this many days before it is due, so frames are welded in time to be coated and shipped. 0 dates them as To make does."
      />
      <WeldingDaysRow
        settings={settings} column="welding_horizon_days" min={1} max={30} onSaved={onSaved}
        label="How far ahead welding looks"
        helper="The Welding page counts what is late, due today, and due within this many days. Later orders are shown but not counted."
      />
    </div>
  );
}

// #746, Session 79.22b.6.4.1 -- ONE OF WELDING'S TWO SETTINGS (746 D7, D26): a
// whole number of calendar days. Saved when the person leaves the box or
// presses Enter, then READ BACK as Who loads your trucks is, because PostgREST
// reports zero rows changed as success (#492's family). A decimal, an empty box
// or a number outside the range is refused with the copy table's own sentence
// and nothing is written - a refusal that answers, never a silent one; the
// database's CHECK holds the same range on its own. The box is 40 to a finger
// and 32 to a mouse, from the app's own control table (#417 D2), and 16px type
// (locked rule 12). Module level -- locked rule 2.
function WeldingDaysRow({ settings, column, label, helper, min, max, onSaved }) {
  const { coarse } = useTier();
  const stored = settings[column];
  const [text, setText] = useState(stored == null ? '' : String(stored));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => { setText(stored == null ? '' : String(stored)); }, [stored]);

  async function commit() {
    const typed = text.trim();
    if (!/^\d+$/.test(typed) || Number(typed) < min || Number(typed) > max) {
      setSaved(false);
      setError(`Choose a number from ${min} to ${max}.`);
      return;
    }
    const next = Number(typed);
    setText(String(next));
    if (next === stored) { setError(''); return; }

    setSaving(true);
    setError('');
    setSaved(false);
    const { data, error: updErr } = await supabase
      .from('company_settings')
      .update({ [column]: next, updated_at: new Date().toISOString() })
      .eq('id', settings.id)
      .select(column);
    setSaving(false);

    if (updErr) { setError('Could not save that. ' + updErr.message); return; }
    if (!data || data.length === 0) {
      setError('Nothing was saved — the change did not reach the database. Reload and try again.');
      return;
    }
    if (data[0][column] !== next) {
      setError('The database kept a different setting. Reload to see what it holds.');
      return;
    }
    setSaved(true);
    await onSaved();
  }

  const id = `setting-${column}`;
  return (
    <div style={{ padding: '14px 0', borderTop: '0.5px solid #E0E0E0' }}>
      <label htmlFor={id} style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={text}
          disabled={saving}
          aria-describedby={`${id}-help`}
          onChange={e => { setText(e.target.value); setSaved(false); }}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          style={{
            // 40 to a finger, 32 to a mouse: a typing box takes the mouse's
            // default rather than its 24px minimum (#417 D2).
            width: 72, height: Math.max(controlHeight(coarse, 'min'), 32), border: '1px solid #E0E0E0', borderRadius: 8,
            padding: '0 10px', fontSize: 16, boxSizing: 'border-box', background: 'white', textAlign: 'right',
          }}
        />
        <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>days</span>
      </div>
      <p id={`${id}-help`} style={{ fontSize: 12, color: 'var(--ink-2)', margin: '8px 0 0', lineHeight: 1.5 }}>
        {helper}
      </p>
      {error && (
        <div role="alert" style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginTop: 10 }}>
          {error}
        </div>
      )}
      {saved && !error && (
        <p style={{ fontSize: 12, color: 'var(--green-xdk)', margin: '10px 0 0' }}>Saved.</p>
      )}
    </div>
  );
}

// Issue #210 — dollar-amount purchase approval threshold. Off by default so no
// company is forced into a spend gate; the amount field is inert until enabled.
function PurchaseApprovalSection({ settings, onSaved }) {
  const [enabled, setEnabled] = useState(!!settings.purchase_approval_threshold_enabled);
  const [amount, setAmount] = useState(settings.purchase_approval_threshold_amount ?? 500);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    const { error: rpcError } = await supabase.rpc('update_purchase_approval_settings', {
      p_enabled: enabled,
      p_threshold_amount: enabled ? (parseFloat(amount) || 0) : null,
      p_reason: reason || null,
    });
    setSaving(false);
    if (rpcError) { setError('Failed to save: ' + rpcError.message); return; }
    setReason('');
    await onSaved();
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 10px', paddingBottom: 8, borderBottom: '0.5px solid #E0E0E0',
      }}>
        Purchase approval threshold
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>Require approval above a dollar amount</p>
          <p style={{ fontSize: 12, color: '#5C5C5C', margin: 0, lineHeight: 1.5 }}>Off by default. Orders never hold for approval unless this is on. Admin and executive only can approve.</p>
        </div>
        <Toggle value={enabled} onChange={setEnabled} />
      </div>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0', opacity: enabled ? 1 : 0.4 }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Threshold amount</p>
        <div style={{ position: 'relative', maxWidth: 160 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#5C5C5C' }}>$</span>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} disabled={!enabled}
            style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px 0 22px', fontSize: 16, boxSizing: 'border-box', background: enabled ? 'white' : '#F5F5F3' }} />
        </div>
      </div>

      <div style={{ padding: '14px 0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Reason for this change (optional)</p>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. setting a spend gate for Q4"
          style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, boxSizing: 'border-box', marginBottom: 12 }} />
        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '10px 20px', background: saving ? '#E0E0E0' : '#1D9E75', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// Issue #287 — purchase-order numbering. Not part of the generic `sections` array
// because the format is a text field and the three settings interact: the format rule
// only means anything while generation is off, and the format string only means
// anything while the rule is on. A grouped save keeps them from being left in a
// combination nobody chose. Single-table, so a plain .update() rather than an RPC —
// Engineering Standards rule 20 governs writes that span tables.
//
// There is deliberately no toggle for uniqueness. It is an invariant enforced by a
// partial unique index on material_requests.po_ref in all three tiers, and a switch
// that can disable a safety rule is an invitation to disable it.
function PoNumberingSection({ settings, onSaved }) {
  const [generate, setGenerate] = useState(settings.po_number_generation_enabled !== false);
  const [enforce, setEnforce] = useState(!!settings.po_number_format_enforced);
  const [format, setFormat] = useState(settings.po_number_format || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    const { error: updErr } = await supabase
      .from('company_settings')
      .update({
        po_number_generation_enabled: generate,
        po_number_format_enforced: generate ? false : enforce,
        po_number_format: (!generate && enforce && format.trim()) ? format.trim() : null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', settings.id);
    setSaving(false);
    if (updErr) { setError('Failed to save: ' + updErr.message); return; }
    await onSaved();
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 10px', paddingBottom: 8, borderBottom: '0.5px solid #E0E0E0',
      }}>
        Purchase order numbering
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>Stede assigns PO numbers</p>
          <p style={{ fontSize: 12, color: '#5C5C5C', margin: 0, lineHeight: 1.5 }}>Every order gets a number automatically, like DAL-PO-00001. Turn this off only if your company already has its own numbering.</p>
        </div>
        <Toggle value={generate} onChange={setGenerate} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderBottom: '0.5px solid #E0E0E0', opacity: generate ? 0.4 : 1 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>Require a format for typed numbers</p>
          <p style={{ fontSize: 12, color: '#5C5C5C', margin: 0, lineHeight: 1.5 }}>
            Numbers must match the pattern below.{generate ? ' Only applies when Stede is not assigning them.' : ''}
          </p>
        </div>
        <Toggle value={!generate && enforce} onChange={val => { if (!generate) setEnforce(val); }} />
      </div>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0', opacity: (!generate && enforce) ? 1 : 0.4 }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>PO number format</p>
        <input
          type="text" value={format} onChange={e => setFormat(e.target.value)}
          disabled={generate || !enforce} placeholder="DR-2026-0000"
          style={{ width: '100%', maxWidth: 260, height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, boxSizing: 'border-box', background: (!generate && enforce) ? 'white' : '#F5F5F3', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
        />
        <p style={{ fontSize: 12, color: '#5C5C5C', margin: '8px 0 0', lineHeight: 1.5 }}>
          Write an example of a real number. A <strong>0</strong> stands for any digit and an <strong>A</strong> for any letter; everything else has to appear exactly as you type it.
        </p>
      </div>

      <div style={{ padding: '14px 0' }}>
        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '10px 20px', background: saving ? '#E0E0E0' : '#1D9E75', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// #289, Session 74z2. The new-user default for invoice reconciliation, rendered
// in New manager defaults where a person looks for it.
//
// It saves immediately on toggle, matching the two generic rows it sits beside,
// rather than waiting for a Save button the rest of the group does not have.
// The other three RPC arguments are passed through from current settings
// unchanged so this cannot clobber the discrepancy thresholds — the RPC updates
// all four columns in one statement and there is no partial-update form of it.
//
// Defined at top level, not inside CompanySettingsPage's body: a component
// declared inside a parent remounts on every render and loses focus and state
// (locked rule 2).
function NewManagerReconcileRow({ settings, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleChange(val) {
    setSaving(true);
    setError('');
    const { error: rpcError } = await supabase.rpc('update_invoice_discrepancy_settings', {
      p_mode: settings.invoice_discrepancy_mode || 'both',
      p_threshold_percent: parseFloat(settings.invoice_discrepancy_threshold_percent) || 0,
      p_threshold_dollars: parseFloat(settings.invoice_discrepancy_threshold_dollars) || 0,
      p_new_manager_default: val,
      p_reason: null,
    });
    setSaving(false);
    if (rpcError) { setError('Could not save that. ' + rpcError.message); return; }
    await onSaved();
  }

  return (
    <>
      <SettingRow
        label="New managers and purchasing users can reconcile invoices"
        desc="Off by default, so nobody can approve a bill until an executive grants it. Existing people are unaffected — set theirs individually in User Management, where granting this alongside Vendor Catalog access is flagged as a segregation-of-duties conflict."
        value={settings.new_manager_invoice_reconciliation_default === true}
        onChange={saving ? () => {} : handleChange}
        isLast
      />
      {error && (
        <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>
      )}
    </>
  );
}

// Issue #191 — invoice discrepancy config. Not part of the generic `sections` array
// above because it's a multi-field grouped save with a reason (calls the
// update_invoice_discrepancy_settings RPC), not a single boolean toggle.
//
// #289, Session 74z2: its new-manager-default toggle moved up into New manager
// defaults (see NewManagerReconcileRow). This section still passes the value to
// the RPC, read straight from settings, because the function writes all four
// columns together — dropping the argument would reset the default every time
// somebody edited a threshold.
function InvoiceReconciliationSection({ settings, onSaved }) {
  const [mode, setMode] = useState(settings.invoice_discrepancy_mode || 'both');
  const [thresholdPercent, setThresholdPercent] = useState(settings.invoice_discrepancy_threshold_percent ?? 5);
  const [thresholdDollars, setThresholdDollars] = useState(settings.invoice_discrepancy_threshold_dollars ?? 50);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    const { error: rpcError } = await supabase.rpc('update_invoice_discrepancy_settings', {
      p_mode: mode,
      p_threshold_percent: parseFloat(thresholdPercent) || 0,
      p_threshold_dollars: parseFloat(thresholdDollars) || 0,
      p_new_manager_default: settings.new_manager_invoice_reconciliation_default === true,
      p_reason: reason || null,
    });
    setSaving(false);
    if (rpcError) { setError('Failed to save: ' + rpcError.message); return; }
    setReason('');
    await onSaved();
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 10px', paddingBottom: 8, borderBottom: '0.5px solid #E0E0E0',
      }}>
        Invoice reconciliation
      </p>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>What triggers a discrepancy flag</p>
        <select value={mode} onChange={e => setMode(e.target.value)}
          style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, background: 'white' }}>
          <option value="both">Percentage or dollar amount, whichever trips first</option>
          <option value="percent">Percentage only</option>
          <option value="dollar">Dollar amount only</option>
        </select>
      </div>

      {(mode === 'percent' || mode === 'both') && (
        <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Percentage threshold</p>
          <div style={{ position: 'relative', maxWidth: 160 }}>
            <input type="number" value={thresholdPercent} onChange={e => setThresholdPercent(e.target.value)}
              style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 26px 0 10px', fontSize: 16, boxSizing: 'border-box' }} />
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#5C5C5C' }}>%</span>
          </div>
        </div>
      )}

      {(mode === 'dollar' || mode === 'both') && (
        <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Dollar threshold</p>
          <div style={{ position: 'relative', maxWidth: 160 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#5C5C5C' }}>$</span>
            <input type="number" value={thresholdDollars} onChange={e => setThresholdDollars(e.target.value)}
              style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px 0 22px', fontSize: 16, boxSizing: 'border-box' }} />
          </div>
        </div>
      )}

      <div style={{ padding: '14px 0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Reason for this change (optional)</p>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. tightening after Q3 vendor review"
          style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, boxSizing: 'border-box', marginBottom: 12 }} />
        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '10px 20px', background: saving ? '#E0E0E0' : '#1D9E75', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ── COST BASIS CORRECTION ────────────────────────────────────────────────────
//
// #343 D6, Session 78m. Whether a vendor invoice is allowed to correct what the
// shelf is worth, and how far.
//
// THIS IS A SETTING RATHER THAN A BUILD-TIME DECISION FOR ONE HONEST REASON:
// nobody knows yet which answer suits D&R. #343 asked for two weeks of live
// receiving data before choosing, and it was right to. What D6 changes is that
// the wait no longer blocks the build -- the mode ships as a control and the
// decision data ships on the same screen, so the choice is read off a panel
// instead of delayed by a calendar.
//
// Default is 'variance_only': it measures every gap and changes no existing
// number, which is the only setting it is safe to be wrong about.
//
// Module level, not nested -- locked rule 2.
function CostBasisCorrectionSection({ settings, onSaved }) {
  const [mode, setMode] = useState(settings.cost_basis_correction_mode || 'variance_only');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  // The decision panel. One query over the last 30 days of receipts, with the
  // three figures derived here rather than in SQL -- they are arithmetic over a
  // small result set, and a Postgres function would be a second place for the
  // definition of "settled" to live.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const since = new Date();
      since.setDate(since.getDate() - 30);

      const { data, error: qErr } = await supabase
        .from('inventory_transactions')
        .select('unit_cost_at_transaction, unit_cost_settled')
        .eq('tx_type', 'receive')
        .gte('created_at', since.toISOString());

      if (cancelled || qErr) return;

      const rows = data || [];
      const settled = rows.filter(r => r.unit_cost_settled != null);
      const noCost = rows.filter(r => r.unit_cost_at_transaction == null);

      // Only receipts that had BOTH a receive-time cost and a settled one can
      // contribute a gap. A receipt that arrived with no price at all is a first
      // valuation, not a variance -- D10 -- and averaging it in as a 100% gap
      // would make this figure meaningless in the case D&R hits most.
      const comparable = settled.filter(r => {
        const b = Number(r.unit_cost_at_transaction);
        return Number.isFinite(b) && b !== 0;
      });
      const avgGap = comparable.length
        ? comparable.reduce((acc, r) => {
            const b = Number(r.unit_cost_at_transaction);
            return acc + Math.abs((Number(r.unit_cost_settled) - b) / b) * 100;
          }, 0) / comparable.length
        : null;

      setStats({
        total: rows.length,
        settled: settled.length,
        provisional: rows.length - settled.length,
        noCost: noCost.length,
        comparable: comparable.length,
        avgGap,
      });
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSave() {
    setSaving(true);
    setError('');
    const { error: rpcError } = await supabase.rpc('update_cost_basis_correction_mode', {
      p_mode: mode,
      p_reason: reason || null,
    });
    setSaving(false);
    if (rpcError) { setError('Failed to save: ' + rpcError.message); return; }
    setReason('');
    await onSaved();
  }

  const stat = (label, value, sub) => (
    <div style={{ flex: '1 1 150px', minWidth: 140 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#0F0F0F' }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#5C5C5C', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 10.5, color: '#9A9A9A', marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ marginBottom: '2rem' }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 10px', paddingBottom: 8, borderBottom: '0.5px solid #E0E0E0',
      }}>
        Cost basis correction
      </p>

      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 4px' }}>
          How an invoice price reaches inventory value
        </p>
        <p style={{ fontSize: 12, color: '#7A7A7A', margin: '0 0 8px', lineHeight: 1.5 }}>
          A receipt records what we think something cost. The vendor invoice, weeks later, records what it
          actually cost. This decides what Stede does with the difference.
        </p>
        <select value={mode} onChange={e => setMode(e.target.value)}
          style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, background: 'white' }}>
          <option value="off">Off — invoice prices reach nothing</option>
          <option value="variance_only">Record the gap only — measure it, change no value</option>
          <option value="revalue_on_hand">Revalue stock on hand — correct what is still on the shelf</option>
        </select>
        <p style={{ fontSize: 11.5, color: '#7A7A7A', margin: '8px 0 0', lineHeight: 1.5 }}>
          {mode === 'off' && 'Nothing is recorded and nothing is corrected. This is how Stede behaved before this setting existed.'}
          {mode === 'variance_only' && 'Every gap is measured and written down, but no shelf value changes. Safe to leave here while the numbers below fill in.'}
          {mode === 'revalue_on_hand' && 'The share of a delivery still on the shelf is corrected to the invoiced price. Whatever was already used cannot be corrected, so it is recorded as a variance instead. Nothing posted to the ledger is ever rewritten.'}
        </p>
      </div>

      {/* THE DECISION PANEL. This is the two weeks of live data #343 asked for,
          rendered as a screen instead of a wait. A high no-cost-at-receive count
          argues for revaluing; a small average gap argues it does not much
          matter either way. */}
      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>What the last 30 days say</p>
        <p style={{ fontSize: 12, color: '#7A7A7A', margin: '0 0 12px', lineHeight: 1.5 }}>
          Read this before changing the setting above. It is the evidence, not a summary of the choice.
        </p>
        {!stats ? (
          <div style={{ fontSize: 12, color: '#9A9A9A' }}>Counting…</div>
        ) : stats.total === 0 ? (
          <div style={{ fontSize: 12, color: '#9A9A9A', lineHeight: 1.5 }}>
            No stock has been received in the last 30 days, so there is nothing to measure yet.
            Come back once Dallas has been receiving for a while.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {stat('Receipts settled by an invoice',
              stats.settled + ' of ' + stats.total,
              stats.provisional + ' still provisional')}
            {stat('Arrived with no cost at all',
              stats.noCost,
              'A first valuation when invoiced, not a gap')}
            {stat('Average gap when both are known',
              stats.avgGap == null ? '—' : stats.avgGap.toFixed(1) + '%',
              stats.comparable + ' receipt' + (stats.comparable === 1 ? '' : 's') + ' had both figures')}
          </div>
        )}
      </div>

      <div style={{ padding: '14px 0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Reason for this change (optional)</p>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. switching after the first month of Dallas invoices"
          style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, boxSizing: 'border-box', marginBottom: 12 }} />
        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '10px 20px', background: saving ? '#E0E0E0' : '#1D9E75', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ── NAVIGATION DEFAULTS ──────────────────────────────────────────────────────
//
// #167 Screen 3, D1/D2/D11, Session 78l. The admin half of the two-layer model:
// a role default that a person may then override for themselves in My Profile.
//
// This exists because of a training constraint rather than a severity one.
// landing_defaults has been real data since 78j's migration and there has never
// been a screen for it, so changing where a role lands meant an UPDATE in the
// Supabase SQL editor. That cannot be taught to anyone, and forty Dallas
// onboards arrive in September.
//
// The bar composition is shown and NOT editable, which is a deliberate line
// rather than an unfinished half. What a role's bar contains is settled -- D11
// ordered it, D12 removed Safety until the module ships, and the fifth slot is
// reserved for Safety's return. Making it editable a week before go-live buys
// nothing a person wants and offers a way to strand a role on a bar with no
// Stock button. Personal reordering is D4's job and belongs in My Profile,
// where the blast radius is one account.
function NavigationDefaultsSection({ settings, onSaved }) {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const defaults = settings.landing_defaults || {};

  // AuthContext's own derivations, transcribed rather than guessed:
  // isManager is `role === 'manager' || isAdmin`, so an admin satisfies both.
  // Reproducing that here matters -- a table that computed isManager as
  // `role === 'manager'` would offer an admin a different choice list than the
  // one they actually get. Locked rule 8: read the predicate, do not infer it.
  function flagsFor(role) {
    const isAdmin = role === 'admin';
    return {
      isAdmin,
      isExecutive: role === 'executive',
      isManager: role === 'manager' || isAdmin,
      isTeamMember: role === 'team_member',
      isPurchasing: role === 'purchasing',
      // Invoicing is a capability, not a role (#289): Jennifer and Bridgette
      // are both `purchasing` with opposite invoice_reconciliation_enabled
      // flags. The table shows what the role gets when the capability is on,
      // and the note under it says so, rather than showing a bar no individual
      // necessarily has.
      profile: { role, invoice_reconciliation_enabled: true },
    };
  }

  async function handleChange(role, pageKey) {
    setSaving(role);
    setError('');
    const next = { ...defaults, [role]: pageKey };
    const { error: updErr } = await supabase
      .from('company_settings')
      .update({ landing_defaults: next, updated_at: new Date().toISOString() })
      .eq('id', settings.id);
    setSaving(null);
    if (updErr) { setError('Could not save that. ' + updErr.message); return; }
    await onSaved();
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 4px', paddingBottom: 8,
        borderBottom: '0.5px solid #E0E0E0',
      }}>
        Navigation
      </p>
      <p style={{ fontSize: 13, color: '#5C5C5C', margin: '10px 0 14px' }}>
        Where each role lands after signing in, and what their phone's bottom bar
        holds. Applies to everyone who has not chosen their own in My profile.
      </p>

      {ROLE_ORDER.map(role => {
        const flags = flagsFor(role);
        // #746, Session 79.22b.6.3 -- through the page rule's one helper, like the
        // other seven places. A ROLE has no branch, so Welding (a branch page,
        // 746 D5) is offered only to the roles that see every branch: admins and
        // executives. A welder chooses it for themselves in My profile.
        const choices = landingChoicesFor(pageRuleAuth(flags));
        const current = defaults[role] || '';
        const bar = barLabelsForRole(role, {
          canSeePurchasing: !flags.isTeamMember,
          canSeeInsights: !flags.isTeamMember,
          canSeeInvoicing: flags.isPurchasing || flags.isAdmin || flags.isExecutive,
        });
        return (
          <div key={role} style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            gap: 16, padding: '14px 0', borderBottom: '0.5px solid #F0F0F0',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0F0F0F' }}>
                {ROLE_LABELS[role].name}
              </div>
              <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 2 }}>
                {ROLE_LABELS[role].gloss}
              </div>
              <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 6 }}>
                Bar: {bar.join(' · ')}
              </div>
              {/* #746 D24, Session 79.22b.6.3 -- gate 746's copy table, word for
                  word. Welding is placed by person, not by role (it depends on
                  the person's branch and switch), so this table cannot draw it;
                  it says so under the one role whose bar has room for it. */}
              {role === 'team_member' && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                  Team members who can record welds also get Welding on their bar.
                </div>
              )}
            </div>
            <select
              value={current}
              disabled={saving === role}
              onChange={e => handleChange(role, e.target.value)}
              style={{
                fontSize: 16, padding: '8px 10px', borderRadius: 8,
                border: '1px solid #D8D8D8', background: 'white', color: '#0F0F0F',
                minWidth: 190, flexShrink: 0,
              }}
            >
              {!current && <option value="">Not set</option>}
              {choices.map(c => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          </div>
        );
      })}

      <p style={{ fontSize: 12, color: '#7A7A7A', margin: '12px 0 0' }}>
        Scan always holds the centre and cannot be moved. Invoicing appears for
        purchasing users who are allowed to reconcile invoices. Safety rejoins
        every bar when that module ships.
      </p>
      {error && <p style={{ fontSize: 13, color: '#A32D2D', margin: '8px 0 0' }}>{error}</p>}
    </div>
  );
}
